import { Link } from 'react-router-dom';
import { Calendar, Building2, Link2, Stethoscope, User, Wallet, EyeOff } from 'lucide-react';
import { cn, getInitials } from '@/lib/utils';
import {
  PRIORIDAD_ESTILO, VENCIMIENTO_ESTILO, estadoVencimiento, formatearVencimiento,
} from '@/lib/tareas';
import type { EtiquetaTarea, PrioridadTarea, Tarea, TareaAsignado, TareaRelacion } from '@/types/tareas';

export function PrioridadBadge({ prioridad, className }: { prioridad: PrioridadTarea; className?: string }) {
  const estilo = PRIORIDAD_ESTILO[prioridad];
  return (
    <span className={cn('inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[11px] font-medium', estilo.badge, className)}>
      <span className={cn('w-1.5 h-1.5 rounded-full', estilo.dot)} />
      {estilo.label}
    </span>
  );
}

export function EtiquetaChip({ etiqueta }: { etiqueta: Pick<EtiquetaTarea, 'nombre' | 'color'> }) {
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium text-white max-w-[9rem] truncate"
      style={{ backgroundColor: etiqueta.color }}
      title={etiqueta.nombre}
    >
      {etiqueta.nombre}
    </span>
  );
}

export function AvatarsAsignados({ asignados, max = 3 }: { asignados: TareaAsignado[]; max?: number }) {
  if (!asignados.length) return null;
  const visibles = asignados.slice(0, max);
  const extra = asignados.length - visibles.length;
  return (
    <div className="flex -space-x-1.5">
      {visibles.map(a => (
        <span
          key={a.id}
          title={a.nombre}
          className="w-6 h-6 rounded-full border-2 border-white bg-primary-100 text-primary-700 text-[10px] font-semibold flex items-center justify-center overflow-hidden"
        >
          {a.fotoUrl ? <img src={a.fotoUrl} alt={a.nombre} className="w-full h-full object-cover" /> : getInitials(a.nombre)}
        </span>
      ))}
      {extra > 0 && (
        <span className="w-6 h-6 rounded-full border-2 border-white bg-gray-100 text-gray-600 text-[10px] font-semibold flex items-center justify-center">
          +{extra}
        </span>
      )}
    </div>
  );
}

export function VencimientoTexto({ tarea, className }: { tarea: Pick<Tarea, 'fechaVencimiento' | 'completadaAt'>; className?: string }) {
  if (!tarea.fechaVencimiento) return null;
  const estado = estadoVencimiento(tarea);
  return (
    <span className={cn('inline-flex items-center gap-1 text-xs', VENCIMIENTO_ESTILO[estado], className)}>
      <Calendar size={12} />
      {formatearVencimiento(tarea.fechaVencimiento)}
    </span>
  );
}

export const ICONO_RELACION = {
  paciente: User,
  consulta: Stethoscope,
  cita: Calendar,
  presupuesto: Wallet,
} as const;

export function rutaDeRelacion(rel: Pick<TareaRelacion, 'tipo' | 'entidadId' | 'pacienteId' | 'citaId' | 'oculto'>): string | null {
  if (rel.oculto || !rel.entidadId) return null;
  if (rel.tipo === 'paciente') return `/pacientes/${rel.entidadId}`;
  // La pantalla de consulta se identifica por el id de la cita, también para una consulta.
  if (rel.tipo === 'cita' || rel.tipo === 'consulta') {
    if (rel.citaId) return `/consultas/${rel.citaId}`;
    return rel.pacienteId ? `/pacientes/${rel.pacienteId}` : null;
  }
  return rel.pacienteId ? `/pacientes/${rel.pacienteId}` : null;
}

export function etiquetaDeRelacion(rel: Pick<TareaRelacion, 'tipo' | 'label' | 'fecha' | 'oculto'>): string {
  if (rel.oculto || !rel.fecha) return rel.label;
  const fecha = new Date(rel.fecha).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  return `${rel.label} · ${fecha}`;
}

/** Cuando la relación está oculta (paciente con ARCO) no se muestra nombre ni se ofrece enlace. */
export function RelacionChip({
  relacion, compacta = false, onQuitar,
}: { relacion: TareaRelacion; compacta?: boolean; onQuitar?: () => void }) {
  const Icono = relacion.oculto ? EyeOff : ICONO_RELACION[relacion.tipo] ?? Link2;
  const ruta = rutaDeRelacion(relacion);
  const contenido = (
    <>
      <Icono size={12} className="flex-shrink-0" />
      <span className={cn('truncate', compacta ? 'max-w-[8rem]' : 'max-w-[16rem]')}>{etiquetaDeRelacion(relacion)}</span>
    </>
  );
  const base = cn(
    'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[11px]',
    relacion.oculto ? 'bg-gray-50 text-gray-400 border-gray-200 italic' : 'bg-primary-50 text-primary-700 border-primary-100',
  );
  return (
    <span className="inline-flex items-center gap-1">
      {ruta
        ? <Link to={ruta} onClick={e => e.stopPropagation()} className={cn(base, 'hover:bg-primary-100')}>{contenido}</Link>
        : <span className={base}>{contenido}</span>}
      {onQuitar && (
        <button type="button" onClick={onQuitar} className="text-gray-400 hover:text-red-500 text-xs leading-none" aria-label="Quitar relación">
          ×
        </button>
      )}
    </span>
  );
}

export function ConsultorioTag({ nombre }: { nombre: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-text-secondary">
      <Building2 size={11} /> {nombre}
    </span>
  );
}
